import React, { useState, useEffect, useId } from 'react';
import { createPortal } from 'react-dom';
import './UI.css';

let openModals = 0;
let previousBodyOverflow = '';
let previousRootOverflow = '';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  className?: string; // Para classes extras
  showMaximize?: boolean; // Exibe o botão de maximizar (padrão: oculto)
}

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  footer,
  size = 'md',
  className = '',
  showMaximize = false,
}) => {
  const [isMaximized, setIsMaximized] = useState(false);
  const labelId = useId();

  useEffect(() => {
    if (!isOpen) return;
    if (openModals++ === 0) {
      previousBodyOverflow = document.body.style.overflow;
      previousRootOverflow = document.documentElement.style.overflow;
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
    }
    return () => {
      if (--openModals === 0) {
        document.body.style.overflow = previousBodyOverflow;
        document.documentElement.style.overflow = previousRootOverflow;
      }
    };
  }, [isOpen]);

  // Fecha com ESC
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Determinar classe de tamanho
  let sizeClass = '';
  const currentSize = isMaximized ? 'full' : size;

  if (currentSize === 'lg') sizeClass = 'ns-modal-lg';
  else if (currentSize === 'xl') sizeClass = 'ns-modal-xl';
  else if (currentSize === 'full') sizeClass = 'ns-modal-full';

  return createPortal(
    <div className="ns-modal-overlay" onClick={onClose}>
      <div
        className={`ns-modal ${sizeClass} ${className}${showMaximize ? ' ns-modal-allow-maximize' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelId}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="ns-modal-header">
          <h3 id={labelId}>{title}</h3>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {/* Botão de Maximizar/Restaurar (opt-in via showMaximize) */}
            {showMaximize && (
              <button
                className="ns-modal-toggle"
                onClick={() => setIsMaximized(!isMaximized)}
                title={isMaximized ? "Restaurar tamanho" : "Maximizar"}
              >
                {isMaximized ? '❐' : '□'}
              </button>
            )}
            <button className="ns-modal-close" onClick={onClose} title="Fechar">
              &times;
            </button>
          </div>
        </div>
        <div className="ns-modal-body">{children}</div>
        {footer && <div className="ns-modal-footer">{footer}</div>}
      </div>
    </div>,
    document.body
  );
};
